import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Check, X, Edit, AlertCircle, Loader2, MapPin } from 'lucide-react';
import { useCompanyContext } from '@/hooks/use-company-context';
import { LocationDisplay } from '@/components/fleet/LocationDisplay';

// Labels traduzidos para tipos de combustível
const fuelTypeLabels: Record<string, string> = {
  gasoline: 'Gasolina Comum',
  gasoline_premium: 'Gasolina Aditivada',
  ethanol: 'Etanol',
  diesel: 'Diesel Comum',
  diesel_s10: 'Diesel S10',
  gnv: 'GNV',
  flex: 'Flex (Veículo)'
};

function getFuelTypeLabel(type: string | null) {
  if (!type) return 'Não detectado';
  return fuelTypeLabels[type] || type;
}

export default function FuelReview() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { companyId } = useCompanyContext();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<any>({});

  // Buscar registros pending_review
  const { data: pendingLogs, isLoading } = useQuery({
    queryKey: ['fuel-logs-pending-review', companyId],
    queryFn: async () => {
      if (!companyId) return [];

      const { data, error } = await supabase
        .from('fleet_fuel_logs')
        .select(`
          *,
          fleet_drivers(name),
          fleet_vehicles(plate, make, model),
          fleet_trips(origin, destination)
        `)
        .eq('company_id', companyId)
        .eq('sync_status', 'pending')
        .order('occurred_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    enabled: !!companyId
  });

  // Helper para normalizar CNPJ (remove caracteres não-numéricos)
  const normalizeCnpj = (cnpj?: string) => cnpj?.replace(/\D/g, '') || '';

  // Função para buscar fornecedor existente
  async function findExistingProvider(
    companyId: string,
    latitude: number,
    longitude: number,
    stationCnpj?: string
  ): Promise<string | null> {
    try {
      console.log('🔍 Searching for existing provider...', { latitude, longitude, stationCnpj });

      // 1. Buscar fornecedores existentes do tipo 'gas_station' ou 'posto'
      const { data: providers, error: searchError } = await supabase
        .from('fleet_providers')
        .select('id, name, cnpj, address')
        .eq('company_id', companyId)
        .in('type', ['gas_station', 'posto'])
        .eq('status', 'active');

      if (searchError) {
        console.error('❌ Error searching providers:', searchError);
        return null;
      }

      if (!providers || providers.length === 0) {
        console.log('⚠️ No active gas stations found in database');
        return null;
      }

      console.log(`📋 Found ${providers.length} active gas stations to check`);

      // 2. PRIORIDADE 1: Buscar por CNPJ normalizado (se disponível)
      if (stationCnpj) {
        const targetCnpj = normalizeCnpj(stationCnpj);
        console.log('🔢 Looking for CNPJ:', targetCnpj);
        
        const matchByCnpj = providers.find(p => {
          const providerCnpj = normalizeCnpj(p.cnpj);
          const matches = providerCnpj === targetCnpj;
          if (matches) {
            console.log(`✅ CNPJ match found: ${p.name} (${p.cnpj} -> ${providerCnpj})`);
          }
          return matches;
        });
        
        if (matchByCnpj) {
          console.log('✅ Found provider by CNPJ:', matchByCnpj.name, matchByCnpj.id);
          return matchByCnpj.id;
        }
        
        console.log('⚠️ No CNPJ match found');
      }

      // 3. Buscar endereço via reverse-geocode
      const { data: geocodeData, error: geocodeError } = await supabase.functions.invoke(
        'reverse-geocode',
        { body: { latitude, longitude } }
      );

      if (geocodeError || !geocodeData?.address) {
        console.warn('⚠️ Could not geocode location, skipping address match');
      }

      const address = geocodeData?.address;
      if (address) {
        console.log('📍 Geocoded address:', address);
      }

      // 4. PRIORIDADE 2: Buscar por proximidade de endereço
      // Critério: mesma rua + mesmo número (ou número próximo ±10)
      if (address?.street) {
        const matchByAddress = providers.find(p => {
          const providerAddr = p.address as any;
          if (!providerAddr?.street) return false;
          
          // Normalizar strings para comparação (lowercase, sem acentos)
          const normalize = (str: string) => str.toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
          
          const providerStreet = normalize(providerAddr.street);
          const capturedStreet = normalize(address.street);
          
          // Verificar se a rua é a mesma
          if (providerStreet !== capturedStreet) return false;
          
          // Se tiver número, verificar se é o mesmo ou próximo (±10)
          if (providerAddr.number && address.number) {
            const providerNum = parseInt(providerAddr.number);
            const capturedNum = parseInt(address.number);
            if (!isNaN(providerNum) && !isNaN(capturedNum)) {
              const diff = Math.abs(providerNum - capturedNum);
              console.log(`📏 Address number diff for ${p.name}: ${diff} (${providerNum} vs ${capturedNum})`);
              return diff <= 10; // Tolerância de ±10 números
            }
          }
          
          // Se não tiver número em ambos, considerar match apenas pela rua
          return true;
        });

        if (matchByAddress) {
          console.log('✅ Found provider by address:', matchByAddress.name, matchByAddress.id);
          return matchByAddress.id;
        }
      }

      // 5. PRIORIDADE 3: Buscar por proximidade geográfica
      // Critério: distância < 500m (sem exigir mesma cidade)
      const DISTANCE_THRESHOLD_KM = 0.5; // 500 metros

      const matchByProximity = providers.find(p => {
        const providerAddr = p.address as any;
        if (!providerAddr?.latitude || !providerAddr?.longitude) return false;
        
        // Calcular distância usando fórmula de Haversine
        const lat1 = providerAddr.latitude;
        const lon1 = providerAddr.longitude;
        const lat2 = latitude;
        const lon2 = longitude;
        
        const R = 6371; // Raio da Terra em km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = 
          Math.sin(dLat/2) * Math.sin(dLat/2) +
          Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
          Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const distance = R * c;
        
        console.log(`📏 Distance to ${p.name}: ${(distance * 1000).toFixed(0)}m`);
        
        return distance <= DISTANCE_THRESHOLD_KM;
      });

      if (matchByProximity) {
        console.log('✅ Found provider by proximity:', matchByProximity.name, matchByProximity.id);
        return matchByProximity.id;
      }

      console.log('⚠️ No matching provider found');
      return null;

    } catch (error) {
      console.error('❌ Exception in findExistingProvider:', error);
      return null;
    }
  }

  // Mutation para aprovar
  const approveMutation = useMutation({
    mutationFn: async (log: any) => {
      let stationId: string | null = null;

      // Tentar buscar fornecedor existente se tiver localização
      if (log.location_latitude && log.location_longitude) {
        const stationCnpj = log.ocr_metadata?.station_cnpj;
        stationId = await findExistingProvider(
          companyId,
          log.location_latitude,
          log.location_longitude,
          stationCnpj
        );
        
        if (stationId) {
          console.log('✅ Associating with existing provider:', stationId);
        } else {
          console.log('⚠️ No matching provider found - station_id will remain null');
        }
      }

      const { error } = await supabase
        .from('fleet_fuel_logs')
        .update({ 
          sync_status: 'reviewed',
          station_id: stationId,
          total_value: log.total_value
        })
        .eq('id', log.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fuel-logs-pending-review'] });
      toast({ title: '✅ Abastecimento aprovado' });
    },
    onError: (error) => {
      toast({ title: 'Erro ao aprovar', description: error.message, variant: 'destructive' });
    }
  });

  // Mutation para rejeitar (deleta o registro)
  const rejectMutation = useMutation({
    mutationFn: async (logId: string) => {
      const { error } = await supabase
        .from('fleet_fuel_logs')
        .delete()
        .eq('id', logId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fuel-logs-pending-review'] });
      toast({ title: '🗑️ Abastecimento deletado' });
    },
    onError: (error) => {
      toast({ title: 'Erro ao deletar', description: error.message, variant: 'destructive' });
    }
  });

  // Mutation para editar e aprovar
  const editAndApproveMutation = useMutation({
    mutationFn: async ({ log, updates }: { log: any; updates: any }) => {
      let stationId: string | null = null;

      // Tentar buscar fornecedor existente se tiver localização
      if (log.location_latitude && log.location_longitude) {
        const stationCnpj = log.ocr_metadata?.station_cnpj;
        stationId = await findExistingProvider(
          companyId,
          log.location_latitude,
          log.location_longitude,
          stationCnpj
        );
        
        if (stationId) {
          console.log('✅ Associating with existing provider:', stationId);
        } else {
          console.log('⚠️ No matching provider found - station_id will remain null');
        }
      }

      const { error } = await supabase
        .from('fleet_fuel_logs')
        .update({
          ...updates,
          sync_status: 'reviewed',
          station_id: stationId,
          total_value: updates.total_value ?? log.total_value
        })
        .eq('id', log.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fuel-logs-pending-review'] });
      setEditingId(null);
      toast({ title: '✅ Abastecimento editado e aprovado' });
    },
    onError: (error) => {
      toast({ title: 'Erro ao salvar', description: error.message, variant: 'destructive' });
    }
  });

  function getConfidenceColor(confidence: number) {
    if (confidence > 80) return 'bg-green-500/10 text-green-600 border-green-500/30';
    if (confidence > 60) return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30';
    return 'bg-red-500/10 text-red-600 border-red-500/30';
  }

  function getImageUrl(path: string | null) {
    if (!path) return '/placeholder.svg';
    const { data } = supabase.storage.from('fleet-images').getPublicUrl(path);
    return data.publicUrl;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            Abastecimentos Pendentes de Revisão
            {pendingLogs && pendingLogs.length > 0 && (
              <Badge variant="outline" className="ml-auto bg-yellow-500/10 text-yellow-600">
                {pendingLogs.length} pendente{pendingLogs.length > 1 ? 's' : ''}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pendingLogs?.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhum abastecimento pendente de revisão
            </p>
          ) : (
            <div className="space-y-6">
              {pendingLogs?.map(log => (
                <Card key={log.id} className="border-yellow-500/30">
                  <CardContent className="pt-6">
                    {/* Header com info do motorista e veículo */}
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="font-semibold">{log.fleet_drivers?.name || 'Motorista desconhecido'}</p>
                        <p className="text-sm text-muted-foreground">
                          {log.fleet_vehicles?.plate} - {log.fleet_vehicles?.make} {log.fleet_vehicles?.model}
                        </p>
                        {log.fleet_trips && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Viagem: {log.fleet_trips.origin} → {log.fleet_trips.destination}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(log.occurred_at).toLocaleString('pt-BR')}
                        </p>
                        <LocationDisplay 
                          companyId={companyId}
                          latitude={log.location_latitude}
                          longitude={log.location_longitude}
                        />
                      </div>
                      <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600">
                        ⏳ Pendente
                      </Badge>
                    </div>

                    {/* Galeria de imagens - Apenas 2 imagens (odômetro e cupom) */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs font-medium mb-1">Odômetro</p>
                        <img 
                          src={getImageUrl(log.odometer_image_path)} 
                          alt="Odômetro"
                          className="w-full aspect-video object-cover rounded border"
                        />
                        <Badge className={`mt-1 text-xs ${getConfidenceColor((log.ocr_metadata as any)?.odometer_confidence || 0)}`}>
                          {((log.ocr_metadata as any)?.odometer_confidence || 0).toFixed(0)}% confiança
                        </Badge>
                      </div>
                      <div>
                        <p className="text-xs font-medium mb-1">Cupom Fiscal</p>
                        <img 
                          src={getImageUrl(log.invoice_image_path)} 
                          alt="Cupom"
                          className="w-full aspect-video object-cover rounded border"
                        />
                        <Badge className={`mt-1 text-xs ${getConfidenceColor((log.ocr_metadata as any)?.invoice_confidence || 0)}`}>
                          {((log.ocr_metadata as any)?.invoice_confidence || 0).toFixed(0)}% confiança
                        </Badge>
                      </div>
                    </div>

                    {/* Dados extraídos - Edição inline */}
                    {editingId === log.id ? (
                      <div className="grid grid-cols-5 gap-4 mb-4">
                        <div>
                          <label className="text-xs text-muted-foreground">Odômetro (km)</label>
                          <Input 
                            type="number"
                            value={editData.odometer || log.odometer || ''}
                            onChange={e => setEditData({...editData, odometer: parseFloat(e.target.value)})}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground">Litros</label>
                          <Input 
                            type="number"
                            step="0.001"
                            value={editData.liters || log.liters || ''}
                            onChange={e => setEditData({...editData, liters: parseFloat(e.target.value)})}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground">Valor Total (R$)</label>
                          <Input 
                            type="number"
                            step="0.01"
                            value={editData.total_value ?? (log.ocr_metadata as any)?.total_value_raw ?? ''}
                            onChange={e => setEditData({...editData, total_value: parseFloat(e.target.value)})}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground">Preço/L (R$)</label>
                          <Input 
                            type="number"
                            step="0.01"
                            value={editData.price_unit || log.price_unit || ''}
                            onChange={e => setEditData({...editData, price_unit: parseFloat(e.target.value)})}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground">Tipo</label>
                          <Input 
                            value={editData.fuel_type || log.fuel_type || ''}
                            onChange={e => setEditData({...editData, fuel_type: e.target.value})}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-5 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-muted-foreground">Odômetro</p>
                          <p className="font-semibold">{log.odometer || 'N/A'} km</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Litros</p>
                          <p className="font-semibold">{log.liters || 'N/A'} L</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Valor Total</p>
                          <p className="font-semibold text-green-600">
                            {log.total_value 
                              ? `R$ ${log.total_value.toFixed(2)}` 
                              : (log.ocr_metadata as any)?.total_value_raw 
                                ? `R$ ${((log.ocr_metadata as any).total_value_raw).toFixed(2)}` 
                                : 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Preço/L</p>
                          <p className="font-semibold">
                            R$ {log.price_unit 
                              ? log.price_unit.toFixed(2) 
                              : ((log.ocr_metadata as any)?.total_value_raw && log.liters && log.liters > 0)
                                ? ((log.ocr_metadata as any).total_value_raw / log.liters).toFixed(2)
                                : 'N/A'
                            }
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Tipo</p>
                          <p className="font-semibold">
                            {getFuelTypeLabel(log.fuel_type || (log.ocr_metadata as any)?.detected_fuel_type)}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Ações */}
                    <div className="flex gap-2">
                      {editingId === log.id ? (
                        <>
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => editAndApproveMutation.mutate({ log, updates: editData })}
                            disabled={editAndApproveMutation.isPending}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Salvar e Aprovar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setEditingId(null);
                              setEditData({});
                            }}
                          >
                            Cancelar
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => approveMutation.mutate(log)}
                            disabled={approveMutation.isPending}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Aprovar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setEditingId(log.id);
                              setEditData({
                                odometer: log.odometer,
                                liters: log.liters,
                                fuel_type: log.fuel_type,
                                price_unit: log.price_unit,
                                total_value: log.total_value
                              });
                            }}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Editar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => rejectMutation.mutate(log.id)}
                            disabled={rejectMutation.isPending}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Rejeitar
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
